import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { getSubscription } from '../api/billingAPI';
import { getAuthState } from '../../assets/scripts/login-util';
// .lic-req* classes (the expiry modal below) are defined here, alongside
// header.js's own LicenseModal which uses the same styling.
import './header.css';
import './subscriptionBanner.css';

/**
 * Subscription status banner + one-time expiry modal (Phase 8, Task 6).
 *
 * Rendered above the router in containers/Admin.js, so it is visible on
 * every screen.
 *
 * ==================== IT STAYS QUIET ====================
 *
 * A banner that is always there is a banner nobody reads. This renders
 * NOTHING unless something needs acting on:
 *
 *   - the term ends within 30 days
 *   - the subscription is past due, in grace, or expired
 *
 * A healthy subscription with months left shows no banner at all.
 *
 * It also stays quiet for a tenant with no subscription record. That is
 * a provisioning gap on our side, not something the customer can fix, and
 * telling 770 grandfathered tenants that their subscription is "not set
 * up" would generate support tickets about a problem they do not have.
 *
 * ==================== IT NEVER BLOCKS ====================
 *
 * Purely informational. An EXPIRED subscription no longer gates anything
 * server-side either (see ibbackend/services/entitlementService.js
 * decide()) - it resolves Free-plan entitlements/seats instead of a 403.
 * If this component fails to load it must not degrade anything, so every
 * error path renders null.
 *
 * ==================== THE ONE-TIME EXPIRY MODAL ====================
 *
 * On top of the persistent banner, an EXPIRED tenant also gets a single
 * modal the first time they land in the app each session - the moment
 * they're most likely to actually notice it, rather than relying only on
 * a thin bar at the top nobody scrolled up to see. "Once per session" is
 * a sessionStorage flag, not a login hook: a hard refresh (or a fresh tab)
 * within the same browser session does not re-show it, but a new
 * session/tab does. It is dismissible with no required action - Free-plan
 * access already works, this is a notice, not a gate.
 */

const WARN_WITHIN_DAYS = 30;
// Same literal cleared on logout in assets/scripts/login-util.js
// setAuthState() - keep the two in sync.
const EXPIRED_MODAL_SESSION_KEY = 'ib_expired_modal_shown';

/**
 * Days remaining on a TRIALING row's own end date, not the generic
 * `endDate` (which is typically null while trialing - trials count down
 * on trialEndDate, paid terms on endDate; conflating the two is why this
 * banner used to show nothing at all for a trial, however close to or
 * past its own end).
 */
const trialDaysRemaining = (sub) =>
    sub.trialEndDate ? moment(sub.trialEndDate).diff(moment(), 'days') : null;

/**
 * True once a TRIALING row's own trialEndDate has passed, even though
 * Status hasn't been moved to EXPIRED yet. That transition is the daily
 * `billing.expire` cron's job (services/renewalService.js
 * processExpiries) - which only runs at all when BILLING_CRON_ENABLED is
 * on, and even then only once a day. A trial that ended an hour ago is
 * still, correctly, Status='TRIALING' until that job next runs. Treating
 * that gap as "still fine" would mean nobody sees anything for up to a
 * day (or indefinitely, in a dev environment with the cron off) - so the
 * frontend checks the date itself rather than waiting on the status flip.
 */
const isLapsedTrial = (sub) => {
    if (sub.status !== 'TRIALING') return false;
    const days = trialDaysRemaining(sub);
    return days !== null && days <= 0;
};

/**
 * A FREE-plan subscription is ₹0/year - there is no term to lapse from
 * and nothing to renew (see the "IT NEVER BLOCKS" note above and
 * ibbackend/services/renewalService.js's expireTrials, which clears
 * EndDate/TrialEndDate specifically so a lapsed trial can never reach
 * Status=EXPIRED going forward). This guard is defensive: it stops a
 * still-affected tenant's account (from before that fix) or any future
 * bug from showing "Your Starter plan ended... renew" copy that makes
 * no sense for a plan that was never purchased.
 */
const isRealExpiry = (sub) => sub.status === 'EXPIRED' && sub.planCode !== 'FREE';

/** Anything the banner/modal should treat as "expired", by status or by date. */
const isExpiryLike = (sub) => isRealExpiry(sub) || isLapsedTrial(sub);

/**
 * A Legacy tenant's plan (Plan.IsPublic='N') is never re-purchasable
 * (planService.js getPlanOrThrow blocks it server-side; it's never in
 * the public catalogue) - every "renew" CTA for them must go to the
 * plan picker, not Overview's one-click same-plan renewal, which is a
 * dead end for LEGACY (billing.js's currentPlan is always undefined for
 * it). Task 30.
 */
const isLegacyPlan = (sub) => sub.planCode === 'LEGACY';

const SubscriptionBanner = () => {
    const history = useHistory();
    const [sub, setSub] = useState(null);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    const authUser = getAuthState('user');
    const user     = (authUser && typeof authUser === 'object') ? authUser : {};
    const isOwner  = Number(user.categoryId) === 1;

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const result = await getSubscription();
                if (cancelled) return;
                const subscription = result.subscription;
                setSub(subscription);

                if (subscription && isExpiryLike(subscription) && !sessionStorage.getItem(EXPIRED_MODAL_SESSION_KEY)) {
                    sessionStorage.setItem(EXPIRED_MODAL_SESSION_KEY, '1');
                    setShowExpiredModal(true);
                }
            } catch (err) {
                // Silent. A banner is not worth an error popup, and this
                // runs on every page load.
                if (!cancelled) setSub(null);
            }
        };
        load();

        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!showExpiredModal) return;
        $('#expired-subscription-modal').modal({ backdrop: 'static' });
        $('#expired-subscription-modal').on('hidden.bs.modal', function () {
            $('.modal').hasClass('show') && $('body').addClass('modal-open');
            setShowExpiredModal(false);
        });
    }, [showExpiredModal]);

    const hideModal = () => $('#expired-subscription-modal').modal('hide');
    const goRenew       = () => { hideModal(); history.push('/billing/overview'); };
    const goChangePlan  = () => { hideModal(); history.push('/billing/plans'); };

    // A real paid plan lapsing (Status=EXPIRED) is backend-enforced Free-tier
    // access right now (see entitlementService.js decide()) - the copy can
    // say so accurately, and a same-plan one-click renewal exists because
    // PlanID was never rewritten (Task 6, D5). A trial merely running past
    // its trialEndDate is NOT yet restricted server-side (nothing evaluates
    // trialEndDate against now until the daily expire cron actually moves
    // Status to EXPIRED - see isLapsedTrial's comment above) and never had a
    // plan to "renew" in the first place, so its copy and its one action
    // ("choose a plan") are both different from the real-expiry case.
    const trialLapsed = sub && isLapsedTrial(sub);

    const expiredModal = (showExpiredModal && sub) && (
        <div className="modal fade" id="expired-subscription-modal" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content lic-req">
                    <div className="lic-req__header">
                        <h5 className="lic-req__title">{trialLapsed ? 'Trial ended' : 'Subscription expired'}</h5>
                        <button
                            style={{ display: 'contents' }}
                            type="button"
                            className="lic-req__close"
                            data-dismiss="modal"
                            aria-label="Close">
                            <i className="la la-close"></i>
                        </button>
                    </div>
                    <div className="lic-req__body">
                        <span className="lic-req__icon"><i className="la la-clock-o"></i></span>
                        {trialLapsed ? (<>
                            <h4 className="lic-req__heading">Your trial has ended</h4>
                            <p className="lic-req__text">
                                {sub.trialEndDate
                                    ? `It ended on ${moment(sub.trialEndDate).format('DD MMM YYYY')}. `
                                    : ''}
                                Choose a plan to keep uninterrupted access.
                            </p>
                        </>) : (<>
                            <h4 className="lic-req__heading">
                                Your {sub.planName || sub.planCode} plan has ended
                            </h4>
                            <p className="lic-req__text">
                                {sub.endDate
                                    ? `It ended on ${moment(sub.endDate).format('DD MMM YYYY')}. `
                                    : ''}
                                {isLegacyPlan(sub)
                                    ? 'Your Legacy plan is no longer available for renewal. Choose a new plan to continue, or stay on Free-plan limits.'
                                    : 'You can keep using InsureBox on Free-plan limits until you renew.'}
                            </p>
                        </>)}
                    </div>
                    <div className="lic-req__footer">
                        <button type="button" className="lic-req__later" data-dismiss="modal">
                            {trialLapsed ? 'Maybe later' : 'Continue on Free plan'}
                        </button>
                        {isOwner && (trialLapsed || isLegacyPlan(sub) ? (
                            <button type="button" className="lic-req__buy" onClick={goChangePlan}>
                                Choose a plan
                            </button>
                        ) : (<>
                            <button type="button" className="lic-req__later" onClick={goChangePlan}>
                                Change plan
                            </button>
                            <button type="button" className="lic-req__buy" onClick={goRenew}>
                                Renew now
                            </button>
                        </>))}
                    </div>
                </div>
            </div>
        </div>
    );

    if (!sub) return null;

    // A Legacy tenant in GRACE_PERIOD is treated as a normal active
    // subscriber: no "lapsed" warning, just the usual 30-day countdown,
    // running to graceEndDate (their real end of access) instead of endDate.
    const isLegacyGrace = sub.status === 'GRACE_PERIOD' && isLegacyPlan(sub);
    const effectiveEndDate = isLegacyGrace ? sub.graceEndDate : sub.endDate;
    const daysLeft = effectiveEndDate ? moment(effectiveEndDate).diff(moment(), 'days') : null;
    const trialDaysLeft = trialDaysRemaining(sub);

    let tone = null;
    let text = null;

    if (isRealExpiry(sub)) {
        // Task 6: EXPIRED is a durable Free-tier state, not a block - the
        // tenant kept using the app on Free-plan restrictions the moment
        // this happened (see ibbackend/services/entitlementService.js).
        // Say what plan lapsed, when, and that Free limits apply now.
        tone = 'warning';
        const planLabel = sub.planName || sub.planCode;
        text = sub.endDate
            ? `Your ${planLabel} plan ended on ${moment(sub.endDate).format('DD MMM YYYY')}. You're on Free-plan limits until you renew.`
            : `Your ${planLabel} plan has ended. You're on Free-plan limits until you renew.`;
    } else if (sub.status === 'CANCELLED') {
        tone = 'danger';
        text = 'Your subscription has ended.';
    } else if (sub.status === 'GRACE_PERIOD' && !isLegacyGrace) {
        tone = 'warning';
        text = sub.graceEndDate
            ? `Your subscription lapsed. Access continues until ${moment(sub.graceEndDate).format('DD MMM YYYY')}.`
            : 'Your subscription lapsed and is in its grace period.';
    } else if (sub.status === 'PAST_DUE') {
        tone = 'warning';
        text = 'We could not take your renewal payment.';
    } else if (trialLapsed) {
        // The date has passed but Status hasn't been cron-flipped to
        // EXPIRED yet (see isLapsedTrial's comment) - worded to not claim
        // a Free-tier restriction that isn't actually in effect server-side
        // until that transition happens.
        tone = 'warning';
        text = sub.trialEndDate
            ? `Your premium feature trial ended on ${moment(sub.trialEndDate).format('DD MMM YYYY')}. Choose a plan to keep uninterrupted access.`
            : 'Your premium feature trial has ended. Choose a plan to keep uninterrupted access.';
    } else if (sub.status === 'TRIALING' && trialDaysLeft !== null && trialDaysLeft <= WARN_WITHIN_DAYS) {
        tone = 'warning';
        text = trialDaysLeft === 0
            ? 'Your premium feature trial ends today.'
            : `Your premium feature trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}.`;
    } else if (sub.planCode === 'FREE' && sub.status === 'ACTIVE' && sub.trialEndedOn) {
        // The gap this fills: once expireTrials' cron settles a lapsed
        // trial into Status=ACTIVE (still PlanID=FREE), every branch above
        // stops matching - not TRIALING anymore, not a real expiry (FREE is
        // excluded from that on purpose), trialLapsed requires
        // Status==='TRIALING'. Without this, a tenant living on the Free
        // plan indefinitely has no signal anywhere that they ever had
        // paid-feature access, or that it ended. Informational, not a
        // warning - the trial ending on schedule isn't a problem - so this
        // uses the same 'brand' tone as the trial countdown itself, and
        // (unlike the one-time expiry modal) is recomputed and shown every
        // load for as long as it's true, the same way the countdown above
        // is - not a one-time toast for something the tenant may live with
        // indefinitely.
        tone = 'brand';
        const planLabel = sub.planName || sub.planCode;
        text = `Your 7-day premium feature trial ended on ${moment(sub.trialEndedOn).format('DD MMM YYYY')}. You're on the ${planLabel} plan.`;
    } else if ((sub.status === 'ACTIVE' || isLegacyGrace) && daysLeft !== null && daysLeft >= 0 && daysLeft <= WARN_WITHIN_DAYS) {
        // An explicit status check, matching every other branch above -
        // this is the only one that used to run on daysLeft alone. That
        // gap let a stale EndDate (e.g. left over on a FREE-plan row from
        // the expireTrials incident) show "Your subscription ends today"
        // even though getSubscription now normalizes endDate to null for
        // a lifetime-free tenant (billingRoutes.js) - this check is
        // defense in depth on top of that, for a real paid term only.
        tone = 'brand';
        text = daysLeft === 0
            ? 'Your subscription ends today.'
            : `Your subscription ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`;
    }

    // EXPIRED sends the owner to Overview, where the one-click "Renew my
    // plan" action reactivates the SAME plan they already had without
    // going through the picker grid (Task 6, D5) - everywhere else (a
    // lapsed trial with no prior plan to renew, or a still-active term
    // someone may want to change) the picker itself is the right next step.
    // A Legacy tenant has no "same plan" to reactivate (Task 30), so they
    // always go to the picker regardless of isRealExpiry - this also
    // covers a Legacy GRACE_PERIOD tenant's normal 30-day countdown.
    const canOneClickRenew = isRealExpiry(sub) && !isLegacyPlan(sub);
    const renewPath  = canOneClickRenew ? '/billing/overview' : '/billing/plans';
    const renewLabel = canOneClickRenew ? 'Renew now' : 'Choose a plan';

    return (<>
        {expiredModal}
        {tone && (
            <div className={`sub-banner sub-banner--${tone}`} role="alert">
                <div className="sub-banner__content">
                    <span className="sub-banner__icon"><i className="la la-info-circle"></i></span>
                    <span className="sub-banner__text">{text}</span>
                </div>
                {/* Only the owner can act on it, so only the owner is
                    offered the button. A member sent to a page they cannot
                    use is worse than no button. The banner keeps to one
                    action for space - "Change plan" alongside "Renew
                    now" lives in the richer expiry modal above. */}
                {isOwner && (
                    <button
                        type='button'
                        className="sub-banner__cta"
                        onClick={() => history.push(renewPath)}>
                        {renewLabel} <i className="la la-arrow-right"></i>
                    </button>
                )}
            </div>
        )}
    </>);
};

export default SubscriptionBanner;
